import { useCallback, useEffect, useMemo, useState } from "react"

import { useMatch, useParams } from "react-router-dom"

import { API_MODULE_DOCUMENT, DocumentEditModal } from "@l2r-front/l2r-app-doc"
import { CatalogItemEditModal, useGetViewCenter } from "@l2r-front/l2r-common"
import { useTranslation } from "@l2r-front/l2r-i18n"
import { DeleteIcon, EditLocationIcon, ModeEditIcon, NoteAddRoundedIcon } from "@l2r-front/l2r-icons"
import { MAP_POINT_EDITOR_ID, useMapDispatchContext, useMapStateContext } from "@l2r-front/l2r-map"
import { useIsModuleAvailableForItem, useRoleIsReadOnly } from "@l2r-front/l2r-networks"
import { useAlertsDispatchContext } from "@l2r-front/l2r-query"
import { MoreMenu, useIsMobileDevice } from "@l2r-front/l2r-ui"
import { NavigationOrigins, useNavigateWithSearchParams, useShiftKeyShortcut } from "@l2r-front/l2r-utils"

import { APP_CITY } from "../../../../common/constants/appCity"
import { I18N_NAMESPACE } from "../../../../common/constants/i18n"
import { API_MODULE_EQUIPMENT, MODULE_EQUIPMENT } from "../../../../common/constants/modules"
import { DETAIL_TAB_PARAM_KEY } from "../../../../common/constants/tabKeys"
import { EquipmentAddDocumentForm } from "../../forms/EquipmentAddDocumentForm"
import { EquipmentAddFormPost } from "../../forms/EquipmentAddForm"
import { EquipmentDetailsFormPost } from "../../forms/EquipmentDetailsForm"
import { EquipmentDuplicateFormPost } from "../../forms/EquipmentDuplicateForm"
import { EquipmentLocationEditFormPost } from "../../forms/EquipmentLocationEditForm"
import { useEquipmentDelete } from "../../hooks/mutations/useEquipmentDelete"
import { useEquipment } from "../../hooks/queries/equipment/useEquipments"
import { useEquipmentType } from "../../hooks/queries/equipment/useEquipmentsType"

import { EquipmentDetailsSidebarContent } from "../EquipmentDetailsSidebarContent"

import * as Styled from "./EquipmentDetailSidebar.styled"

export function EquipmentDetailSidebar() {

    const [localizationModalOpen, setLocalizationModalOpen] = useState(false)
    const [editModalOpen, setEditModalOpen] = useState(false)
    const [documentAddModalOpen, setDocumentAddModalOpen] = useState(false)
    const [duplicateModalOpen, setDuplicateModalOpen] = useState(false)
    const [newEquipmentLocation, setNewEquipmentLocation] = useState(null)

    const { setAlert } = useAlertsDispatchContext()
    const getViewCenter = useGetViewCenter()
    const { t } = useTranslation(I18N_NAMESPACE)
    const navigate = useNavigateWithSearchParams()
    const { signId: equipmentId, road } = useParams()

    const { data: equipment, isLoading: isLoadingEquipment } = useEquipment(equipmentId)
    const { data: equipmentType } = useEquipmentType(equipment?.equipment_type, {}, { enabled: !!equipment })

    const readOnly = useRoleIsReadOnly(equipment?.sub_networks, API_MODULE_EQUIPMENT)
    const isMobile = useIsMobileDevice()
    const matchRoadCode = useMatch(`:slugId/${APP_CITY}/${MODULE_EQUIPMENT}/:road/code/:code/*`)
    const documentAvailable = useIsModuleAvailableForItem(equipment?.sub_networks, API_MODULE_DOCUMENT)

    const { resetMapBoundingBox, setMapPin } = useMapDispatchContext()
    const latLong = equipment?.geometry.coordinates
    const { storedBoundingBox } = useMapStateContext()

    const title = useMemo(() => {
        return equipmentType?.name ? equipmentType.name : ""
    }, [equipmentType])

    const navigateBack = useCallback(() => {
        setMapPin()
        navigate(matchRoadCode ? `../${road}/code/${matchRoadCode.params.code}` : `../${road}`, { paramsToDelete: [DETAIL_TAB_PARAM_KEY], navigationOrigin: NavigationOrigins.ROAD_FROM_BACK_DETAIL })
    }, [matchRoadCode, navigate, road, setMapPin])

    const deleteMutation = useEquipmentDelete(equipmentId, {
        onSuccess: () => {
            setMapPin()
            navigate(`../${road}`)
        },
        errorSnackbarMessage: t(I18N_NAMESPACE, "containers.equipmentDetailSidebar.edit.delete.error"),
        successSnackbarMessage: t(I18N_NAMESPACE, "containers.equipmentDetailSidebar.edit.delete.success"),
    })

    const handleDeleteEquipment = useCallback(() => {
        setAlert({
            alertTitle: t(I18N_NAMESPACE, "containers.equipmentDetailSidebar.edit.delete.confirmation", { itemName: equipmentType?.name }),
            cancelText: t(I18N_NAMESPACE, "containers.equipmentDetailSidebar.edit.delete.cancel"),
            confirmText: t(I18N_NAMESPACE, "containers.equipmentDetailSidebar.edit.delete.confirm"),
            callback: () => {
                deleteMutation.mutate()
            },
            severity: "warning",
        })
    }, [equipmentType, deleteMutation, setAlert, t])

    const anyModalOpen = localizationModalOpen || editModalOpen || documentAddModalOpen || duplicateModalOpen || !!newEquipmentLocation
    useShiftKeyShortcut("d", () => setDuplicateModalOpen(true), !!equipment && !readOnly && !anyModalOpen)
    useShiftKeyShortcut("n", () => setNewEquipmentLocation(getViewCenter()), !!equipment && !readOnly && !anyModalOpen)
    useShiftKeyShortcut("e", () => setEditModalOpen(true), !!equipment && !readOnly && !anyModalOpen)

    useEffect(() => {
        if (!localizationModalOpen && !duplicateModalOpen && !newEquipmentLocation && storedBoundingBox?.[MAP_POINT_EDITOR_ID]) {
            resetMapBoundingBox(null, MAP_POINT_EDITOR_ID)
        }
    }, [localizationModalOpen, duplicateModalOpen, newEquipmentLocation, resetMapBoundingBox, storedBoundingBox])

    if (isLoadingEquipment || !equipment) {
        return null
    }

    return <>
        <Styled.BottomSheetContent id="equipmentDetailSidebar-sidebar"
            isMobile={isMobile}
            onBack={navigateBack}
            title={title}
            titleSideComponent={<MoreMenu
                menuConfig={[
                    {
                        displayed: !readOnly,
                        id: "edit-location",
                        label: t(I18N_NAMESPACE, "containers.equipmentDetailSidebar.edit.location"),
                        icon: <EditLocationIcon color="common/black_states" />,
                        props: {
                            onClick: () => setLocalizationModalOpen(true),
                        },
                    },
                    {
                        displayed: !readOnly,
                        id: "edit-properties",
                        label: t(I18N_NAMESPACE, "containers.equipmentDetailSidebar.edit.properties", { itemName: equipmentType?.name }),
                        icon: <ModeEditIcon color="common/black_states" />,
                        props: {
                            onClick: () => setEditModalOpen(true),
                        },
                    },
                    {
                        displayed: documentAvailable && !readOnly,
                        id: "add-document",
                        label: t(I18N_NAMESPACE, "containers.equipmentDetailSidebar.addDocument"),
                        icon: <NoteAddRoundedIcon color="common/black_states" />,
                        props: {
                            onClick: () => setDocumentAddModalOpen(true),
                        },
                    },
                    {
                        displayed: !readOnly,
                        id: "delete-equipment",
                        label: t(I18N_NAMESPACE, "containers.equipmentDetailSidebar.edit.delete.menu", { itemName: equipmentType?.name }),
                        icon: <DeleteIcon color="common/black_states" />,
                        props: {
                            onClick: () => handleDeleteEquipment(),
                        },
                    },
                ]}
            />}
        >
            <EquipmentDetailsSidebarContent equipmentType={equipmentType} />
        </Styled.BottomSheetContent>
        <CatalogItemEditModal
            FormComponent={EquipmentLocationEditFormPost}
            formTitle={t(I18N_NAMESPACE, "containers.equipmentLocationEditForm.title")}
            signId={equipmentId}
            startingPosition={{ longitude: latLong[0], latitude: latLong[1] }}
            startingRoad={equipment?.linear_location.road}
            open={localizationModalOpen}
            onClose={() => setLocalizationModalOpen(false)}
        />
        {newEquipmentLocation && <CatalogItemEditModal
            formTitle={t(I18N_NAMESPACE, "containers.equipmentAddForm.title")}
            typeTitle={t(I18N_NAMESPACE, "containers.equipmentDetailsForm.type")}
            contentFieldLabel={t(I18N_NAMESPACE, "containers.equipmentDetailsForm.content")}
            FormComponent={EquipmentAddFormPost}
            startingPosition={newEquipmentLocation}
            requirePositionChange
            open={!!newEquipmentLocation}
            onClose={() => setNewEquipmentLocation(null)}
        />}
        <CatalogItemEditModal
            FormComponent={EquipmentDuplicateFormPost}
            formTitle={t(I18N_NAMESPACE, "containers.equipmentDuplicateForm.title")}
            equipment={equipment}
            startingPosition={{ longitude: latLong[0], latitude: latLong[1] }}
            startingRoad={equipment?.linear_location.road}
            open={duplicateModalOpen}
            onClose={() => setDuplicateModalOpen(false)}
        />
        <CatalogItemEditModal
            formTitle={t(I18N_NAMESPACE, "containers.equipmentDetailsForm.title")}
            typeTitle={t(I18N_NAMESPACE, "containers.equipmentDetailsForm.type")}
            contentFieldLabel={t(I18N_NAMESPACE, "containers.equipmentDetailsForm.content")}
            FormComponent={EquipmentDetailsFormPost}
            signId={equipmentId}
            open={editModalOpen}
            onClose={() => setEditModalOpen(false)}
        />
        {documentAddModalOpen && <DocumentEditModal
            open={documentAddModalOpen}
            onClose={() => setDocumentAddModalOpen(false)}
            FormComponent={EquipmentAddDocumentForm}
            equipmentId={equipmentId}
        />}
    </>
}